/**
 * Who the walkthrough is for. Each audience has its own ordered list of at most
 * five Easy Read steps in {@link TOUR_STEPS}:
 * - `member` — Members (service recipients); also the fallback for unknown roles
 * - `independent` — parents, guardians, support coordinators and other contacts
 * - `provider` — Provider Basic / Admin / Owner (L2–L4)
 * - `provider-psr` — providers holding the admin "Premium Session Recording" switch
 */
export type TourAudienceKind = 'member' | 'independent' | 'provider' | 'provider-psr'

/** Copy variants applied on top of an audience's step (body overrides only). */
export type TourStepVariant = 'consent-giver'

export type TourStep = {
  /** Stable per slot (e.g. `messages`); the same id may carry different copy per audience. */
  id: string
  /**
   * CSS selector for the element to spotlight — a `data-tour` anchor rendered
   * on both the desktop icon rail and the mobile bottom nav. When no visible
   * match exists on the current screen, the step is skipped.
   */
  target: string
  title: string
  /** Easy Read: plain words, short sentences. */
  body: string
  /** Only for Independents who follow someone's sessions (the tab is absent otherwise). */
  sessionFollowerOnly?: boolean
  /** Body overrides for users who match a variant. */
  variants?: Partial<Record<TourStepVariant, Pick<TourStep, 'body'>>>
}

export type TourAudience = {
  /** Provider role (runs sessions). */
  provider?: boolean
  /** Holds the admin "Premium Session Recording" switch (providers only). */
  premiumSessionRecording?: boolean
  /** Independent role (family, guardian, support coordinator, other contact). */
  independent?: boolean
  /** An Independent who sees the Sessions tab because they follow someone's sessions. */
  sessionFollower?: boolean
  /** An Independent marked "Can give consent" on at least one association. */
  consentGiver?: boolean
}

export const MAX_TOUR_STEPS_PER_AUDIENCE = 5

const MESSAGES = '[data-tour="messages"]'
const SESSIONS = '[data-tour="nav-sessions"]'
const CALLS = '[data-tour="nav-calls"]'
const ALERTS = '[data-tour="notifications"]'
const MENU = '[data-tour="menu"]'

/**
 * Easy Read: plain words, at most 3 sentences per step, short sentences. Each
 * step is one core feature, in the same left-to-right order as the mobile
 * bottom nav (Sessions, Messages, Calls, Alerts, Menu). Forum and Announce are
 * not core, so they are a single closing sentence in the Messages step.
 */

const MEMBER_SESSIONS: TourStep = {
  id: 'sessions',
  target: SESSIONS,
  title: 'Your sessions',
  body: 'Your sessions are here. Tap one to open it. We show when your support worker is on the way and when they arrive.',
}

const FOLLOWER_SESSIONS: TourStep = {
  id: 'follow-sessions',
  target: SESSIONS,
  title: 'Sessions you follow',
  body: 'The sessions of the person you support are here. Tap one to open it. We show when the support worker is on the way, arrives and finishes.',
  sessionFollowerOnly: true,
}

const PROVIDER_SESSIONS: TourStep = {
  id: 'sessions',
  target: SESSIONS,
  title: 'Your sessions',
  body: 'Open a session here, then tap Navigate & start journey when you leave. Tap Record arrival when you get there. For phone or video sessions, tap Start call or Start video call.',
}

const RECORD_SESSIONS: TourStep = {
  // The record card only exists inside an in-progress session, so this
  // anchors on the Sessions tab like the step before it.
  id: 'record-sessions',
  target: SESSIONS,
  title: 'Record whole sessions',
  body: 'In an in-person session, tap Record whole session. Always ask first. Then check the note before you approve it.',
}

const MEMBER_MESSAGES: TourStep = {
  id: 'messages',
  target: MESSAGES,
  title: 'Messages',
  body: 'Send a message to your support team here. Tap a name to start. News and questions are in Forum.',
}

const INDEPENDENT_MESSAGES: TourStep = {
  id: 'messages',
  target: MESSAGES,
  title: 'Messages',
  body: 'Talk to the support team here. Tap a name to start. News and questions are in Forum.',
}

const PROVIDER_MESSAGES: TourStep = {
  id: 'messages',
  target: MESSAGES,
  title: 'Messages',
  body: 'Chat with your team and the people you support. Tap a name to start. Team news, boards and polls are in Announce.',
}

const PEOPLE_CALLS: TourStep = {
  id: 'calls',
  target: CALLS,
  title: 'Calls',
  body: 'Call your team by voice or video here. All calls are recorded, and video pauses when your phone connects to a car. Tap Call Help to get help fast, or call 000 in an emergency.',
}

const PROVIDER_CALLS: TourStep = {
  id: 'calls',
  target: CALLS,
  title: 'Calls',
  body: 'Make voice and video calls here. All calls are recorded, and video pauses when your phone connects to a car. Tap Call Help to get help fast, or call 000 in an emergency.',
}

const PEOPLE_ALERTS: TourStep = {
  id: 'alerts',
  target: ALERTS,
  title: 'Alerts',
  body: 'Your alerts are here. New messages, missed calls and session updates show up. Tap an alert to open it.',
}

const INDEPENDENT_ALERTS: TourStep = {
  ...PEOPLE_ALERTS,
  variants: {
    'consent-giver': {
      body: 'Your alerts are here, like new messages and missed calls. If a therapist asks you to agree to recording a session, the request shows here. Tap it to say yes or no.',
    },
  },
}

const PROVIDER_ALERTS: TourStep = {
  id: 'alerts',
  target: ALERTS,
  title: 'Alerts',
  body: 'Your alerts are here. New messages, missed calls and session reminders show up. Tap an alert to open it.',
}

const MENU_REPLAY_AND_REPORT =
  'Tap Replay set up or Replay walkthrough to see them again. If something is not working, tap Report a problem.'

const PEOPLE_MENU: TourStep = {
  id: 'menu',
  target: MENU,
  title: 'Menu',
  body: `Menu has your settings, permissions and call recording choice. ${MENU_REPLAY_AND_REPORT}`,
}

const PROVIDER_MENU: TourStep = {
  id: 'menu',
  target: MENU,
  title: 'Menu',
  body: `Menu has your status, settings and permissions. ${MENU_REPLAY_AND_REPORT}`,
}

/**
 * Walkthrough v37, per audience: where to look and where to do things. Targets
 * resolve against `data-tour` anchors on the real nav, which both the desktop
 * rail and the mobile bottom nav render, so the tour stays anchored on either
 * layout. "Report a problem" lives in each Menu step rather than taking a step
 * of its own. Providers with Premium Session Recording swap Alerts for their
 * recording step to stay at five.
 */
export const TOUR_STEPS: Record<TourAudienceKind, readonly TourStep[]> = {
  member: [MEMBER_SESSIONS, MEMBER_MESSAGES, PEOPLE_CALLS, PEOPLE_ALERTS, PEOPLE_MENU],
  independent: [
    FOLLOWER_SESSIONS,
    INDEPENDENT_MESSAGES,
    PEOPLE_CALLS,
    INDEPENDENT_ALERTS,
    PEOPLE_MENU,
  ],
  provider: [
    PROVIDER_SESSIONS,
    PROVIDER_MESSAGES,
    PROVIDER_CALLS,
    PROVIDER_ALERTS,
    PROVIDER_MENU,
  ],
  'provider-psr': [
    PROVIDER_SESSIONS,
    RECORD_SESSIONS,
    PROVIDER_MESSAGES,
    PROVIDER_CALLS,
    PROVIDER_MENU,
  ],
}

/** Which audience's step list this user gets. */
export function tourAudienceKind(audience: TourAudience): TourAudienceKind {
  if (audience.provider) return audience.premiumSessionRecording ? 'provider-psr' : 'provider'
  if (audience.independent || audience.sessionFollower) return 'independent'
  return 'member'
}

/** The steps this user should see: their audience's list, with variants applied. */
export function tourStepsFor(audience: TourAudience): TourStep[] {
  return TOUR_STEPS[tourAudienceKind(audience)]
    .filter((step) => !step.sessionFollowerOnly || audience.sessionFollower === true)
    .map((step) => {
      const override = audience.consentGiver ? step.variants?.['consent-giver'] : undefined
      return override ? { ...step, ...override } : step
    })
}

/**
 * Re-evaluate the steps AFTER `index` for `audience`, keeping the steps
 * already shown (0…index) untouched. Returns `current` itself when nothing
 * changes, so it is safe as a state updater.
 */
export function upcomingStepsFor(current: TourStep[], index: number, audience: TourAudience): TourStep[] {
  const shown = current.slice(0, index + 1)
  const shownIds = new Set(shown.map((s) => s.id))
  const fresh = tourStepsFor(audience)
  // Resume after the furthest point already shown, so a step the user has
  // passed is never inserted behind them.
  const position = Math.max(-1, ...shown.map((s) => fresh.findIndex((f) => f.id === s.id)))
  const rest = fresh.filter((s, i) => i > position && !shownIds.has(s.id))
  const next = [...shown, ...rest]
  const same =
    next.length === current.length &&
    next.every((s, i) => s.id === current[i].id && s.body === current[i].body)
  return same ? current : next
}
